'use client';

import { useCallback, useRef, useSyncExternalStore } from 'react';

type ElementSizes = {
  height: number;
  width: number;
};

const SERVER_SNAPSHOT: ElementSizes = { height: 0, width: 0 };

function getServerSnapshot(): ElementSizes {
  return SERVER_SNAPSHOT;
}

function isEqual(sizesA: ElementSizes, sizesB: ElementSizes) {
  return sizesA.height === sizesB.height && sizesA.width === sizesB.width;
}

function useElementSizes(ref: React.RefObject<HTMLElement | null>): ElementSizes {
  const subscribe = useCallback(
    (onStoreChange: () => void) => {
      const el = ref.current;
      if (!el) return () => {};

      const resizeObserver = new ResizeObserver(() => {
        onStoreChange();
      });

      resizeObserver.observe(el);

      return () => {
        resizeObserver.disconnect();
      };
    },
    [ref]
  );

  const lastResult = useRef(SERVER_SNAPSHOT);

  const getSnapshot = useCallback(() => {
    const el = ref.current;
    const result = {
      height: el?.offsetHeight ?? 0,
      width: el?.offsetWidth ?? 0,
    };

    // Only update the lastResult if the new result is different to avoid infinite loops
    if (!isEqual(result, lastResult.current)) {
      lastResult.current = result;
    }

    return lastResult.current;
  }, [ref]);

  return useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);
}

export { useElementSizes };
